import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";

import type {
  FiCatalogResponse,
  FiExecuteResponse,
  FiFocusArea,
  FiLimits,
  FiSummarizeResponse,
} from "@mapmycustomers/shared/types/entity/fieldInsight";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import nameComparator from "@mapmycustomers/shared/util/comparator/nameComparator";

import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam/selectors";

import {
  executeFieldInsightsChart,
  fetchFieldInsightsCharts,
  fetchFieldInsightsFocusAreas,
  fetchFieldInsightsLimits,
  summarizeFieldInsightsChart,
} from "./actions";

function* onFetchFieldInsightsFocusAreas() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // The API already orders them on displayOrder, so the order is taken as given.
    const focusAreas: FiFocusArea[] = yield callApi("fetchFieldInsightsFocusAreas", orgId);
    yield put(fetchFieldInsightsFocusAreas.success({ focusAreas, organizationId: orgId }));
  } catch (error) {
    yield put(fetchFieldInsightsFocusAreas.failure());
    yield put(handleError({ error }));
  }
}

function* onFetchFieldInsightsLimits() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const limits: FiLimits = yield callApi("fetchFieldInsightsWeeklySummaryLimits", orgId);
    yield put(fetchFieldInsightsLimits.success({ limits, organizationId: orgId }));
  } catch (error) {
    yield put(fetchFieldInsightsLimits.failure());
    yield put(handleError({ displayNotification: false, error }));
  }
}

function* onFetchFieldInsightsCharts({
  payload,
}: ReturnType<typeof fetchFieldInsightsCharts.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<FiCatalogResponse> = yield callApi(
      "fetchFieldInsightsCharts",
      orgId,
      { params: payload }
    );
    yield put(
      fetchFieldInsightsCharts.success({
        charts: [...response.data].sort(nameComparator),
        organizationId: orgId,
        scope: payload,
      })
    );
  } catch (error) {
    yield put(fetchFieldInsightsCharts.failure());
    yield put(handleError({ error }));
  }
}

function* onExecuteFieldInsightsChart({ payload }: ReturnType<typeof executeFieldInsightsChart>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: FiExecuteResponse = yield callApi(
      "executeFieldInsightsChart",
      orgId,
      payload.chartId,
      { ...payload.scope, params: payload.params }
    );
    yield call(payload.callback, response);
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

// Best-effort: a failed summary must not surface a global error toast — it just doesn't render.
function* onSummarizeFieldInsightsChart({
  payload,
}: ReturnType<typeof summarizeFieldInsightsChart>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: FiSummarizeResponse = yield callApi(
      "summarizeFieldInsightsChart",
      orgId,
      payload.chartId,
      payload.request
    );
    yield call(payload.callback, response);
  } catch {
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

export function* fieldInsightsSagas() {
  // takeLatest: the catalog resolves its dropdown options against the requested
  // scope, so a request carrying a newly resolved scope has to supersede one
  // already in flight. Holding the first would leave the options resolved
  // against whatever scope the page happened to mount with.
  yield takeLatest(fetchFieldInsightsCharts.request, onFetchFieldInsightsCharts);
  // Same reasoning: the summary panel and the settings modal both ask for these.
  yield takeLeading(fetchFieldInsightsFocusAreas.request, onFetchFieldInsightsFocusAreas);
  // Org-wide constants: one read serves every consumer, and a second request while one is in
  // flight would fetch the same numbers again.
  yield takeLeading(fetchFieldInsightsLimits.request, onFetchFieldInsightsLimits);
  yield takeLatest(executeFieldInsightsChart, onExecuteFieldInsightsChart);
  yield takeLatest(summarizeFieldInsightsChart, onSummarizeFieldInsightsChart);
}
